import { Remark } from 'react-remark'
import React from 'react'

import {
  ClientUser,
  CompleteMark,
  formatDate,
  formatDateEx,
  formatWithThousandSep,
  showPaymentWreserve,
} from '../utils/utils'
import { formatDocumentTitle } from '../utils/documentTitleUtils'
import { calculatePrintLayout } from '../utils/printLayoutUtils'
import { NvvAction } from '../enums/DocActions'
import { Additional, Payment } from '../interfaces/Reservation'

import DocNotes from './DocNotesV2'
import { DocHeader, DocHeaderType } from './DocHeaderV2'
import { OrganizationFrame, OrganizationFrameType } from './OrganizationFrameV2'
import { ClientFrame, ClientFrameType } from './ClientFrameV2'
import { VehicleFrame, VehicleFrameType } from './VehicleFrameV2'
import { ReservationDet } from './ReservationPreview'
import { DocStatusHeaderEx } from './DocStatusHeader'
import { PayVehicle } from './ReservationNvvEdit'
import { TotalDisplay } from './SalenoteDetail'

const ReservationNvvDetail: React.FC<{
  reservation: ReservationDet
  nvvInd: number
}> = ({ reservation, nvvInd }) => {
  const archivedAt = reservation.archivedAt
  const voidedAt = reservation.voidedAt
  let docHeader: DocHeaderType

  if (nvvInd === NvvAction.SoloReserva) {
    const myNumberRes = reservation?.number === 0 ? 'XX' : reservation?.number

    // Format title for reservation using template configuration
    const defaultReservationTitle = 'Documento de Reserva'
    const formattedReservationTitle = formatDocumentTitle(reservation.titleConfig, defaultReservationTitle, myNumberRes)

    docHeader = {
      urlImage: reservation.body.organization.pictures.logotype,
      titulo: formattedReservationTitle,
      isAnulada: !!voidedAt,
      tituloAnulada: 'NULA',
      fechaIniText: `Fecha`,
      fechaIniValue: formatDate(reservation?.date),
      fechaFinText: '',
      fechaFinValue: '',
    }
  } else {
    const myNumberNvv = reservation?.number === 0 ? 'XX' : reservation?.number

    // Format title for sale note using template configuration
    const defaultSaleNoteTitle = 'Nota de Venta'
    const formattedSaleNoteTitle = formatDocumentTitle(reservation.titleConfig, defaultSaleNoteTitle, myNumberNvv)

    docHeader = {
      urlImage: reservation.body.organization.pictures.logotype,
      titulo: formattedSaleNoteTitle,
      isAnulada: !!voidedAt,
      tituloAnulada: 'NULA',
      fechaIniText: `Fecha`,
      fechaIniValue: formatDate(reservation?.date),
      fechaFinText: '',
      fechaFinValue: '',
    }
  }

  const user = { name: reservation?.profile_name ?? '', phone: reservation.user_phone ?? '' }
  const organizationFrame: OrganizationFrameType = {
    ...reservation.body.organization,
    user: user,
  }
  const clientFrame: ClientFrameType = {
    ...reservation.body.client,
  }

  const vehicleFrame: VehicleFrameType = {
    ...reservation.body.vehicle,
    price: reservation.price,
  }

  const costs = reservation.additionals ?? []
  const payments = reservation.payments ?? []
  const vehicles = reservation.vehicles ?? []

  const clientUser: ClientUser = {
    client: {
      firstname: reservation.body.client.name,
      lastname: '',
      nin: reservation.body.client.nin,
    },
  }

  const footer = CompleteMark(reservation.footer ?? '', clientUser)

  const price = reservation.price
  const porReservar = costs.reduce((accumulator, item) => Number(accumulator) + item.cost, price)
  const reservado =
    payments.reduce((accumulator, item) => Number(accumulator) + item.value, 0) +
    vehicles.reduce((accumulator, item) => Number(accumulator) + item.price, 0)
  const saldo = reservado - porReservar

  let title = ''
  let date = ''
  let by = ''
  let cause = ''

  if (voidedAt) {
    title = 'Esta reserva fue anulada'
    date = voidedAt ?? ''
    cause = reservation.voidedCause ?? ''
    by = reservation?.voidedBy_user ?? ''
  } else if (archivedAt) {
    title = 'Esta reserva fue archivada'
    date = archivedAt ?? ''
    by = reservation?.archivedBy_user ?? ''
  }

  const notesLength = reservation?.notes ? reservation.notes.length : 0

  const layoutConfig = calculatePrintLayout({
    costsCount: costs.length,
    paymentsCount: payments.length,
    vehiclesCount: vehicles.length,
    hasDiscount: false, // Reservations typically don't have the explicit discount breakdown block here
    textLength: notesLength + (footer?.length ?? 0),
  })

  return (
    <>
      <style>{`
        @media print {
          html {
            font-size: ${layoutConfig.rootFontSizePx}px !important;
          }
        }
      `}</style>
      <div className="v2-print-container">
        <DocStatusHeaderEx title={title} date={date} cause={cause} by={by} />
        <DocHeader header={docHeader} />

        <OrganizationFrame organization={organizationFrame} />

        <ClientFrame client={clientFrame} />

        <VehicleFrame vehicle={vehicleFrame} />

        {costs?.length > 0 && <GridCost title="Adicionales" paycosts={costs} />}
        <TotalDisplay title="Total a pagar" total={porReservar} />
        {payments?.length > 0 && <GridPayment title="Pagos" payments={payments} />}
        {vehicles?.length > 0 && <GridVeh title="Vehiculos" paycosts={vehicles} />}
        <TotalDisplay title="Total pagos" total={reservado} />
        {saldo > 0 && <TotalDisplay title="Saldo a favor del cliente" total={saldo} name="saldo" />}
        {saldo < 0 && <TotalDisplay title="Saldo a pagar" total={-1 * saldo} name="saldo" />}

        <div className={`dynamic-print-text ${layoutConfig.pageBreak ? 'print:break-before-page' : ''}`}>
          <DocNotes notes={reservation?.notes} />

          {/* Footer content */}
          <div className="mt-4 print:mt-[1em] text-lg print:text-base">
            <hr className="w-full border-t border-gray-400 my-2 print:my-[0.5em]" />
            <Remark>{footer}</Remark>
          </div>
        </div>
      </div>
    </>
  )
}

const showDescription = (pay: PayVehicle) => {
  return pay.description
}

export const GridVeh: React.FC<{
  title: string
  paycosts: PayVehicle[]
  showPurchaseLinks?: boolean
  isApproved?: boolean
}> = ({ title, paycosts, showPurchaseLinks = false, isApproved = false }) => {
  return (
    <div className="print:break-inside-avoid border border-gray-400 p-4 print:p-1 mt-4 print:mt-1">
      <div className="text-xl print:text-sm font-bold mb-2 print:mb-0 uppercase text-gray-700 border-b border-gray-300 pb-2 print:pb-0">
        {title}
      </div>
      <div className="grid grid-cols-4 text-base print:text-sm gap-y-2 print:gap-y-0 mt-2 print:mt-1">
        {paycosts.map((payment) => (
          <React.Fragment key={payment.key}>
            <div className="pr-2 col-span-3">
              <div>{showDescription(payment)}</div>
              {showPurchaseLinks && isApproved && payment.purchaseCreated && payment.associatedPurchaseId && (
                <div className="text-sm print:text-xs text-blue-600 mt-1 print:mt-0.5 print:hidden">
                  <a href={`/purchases/view/${payment.associatedPurchaseId}`} className="hover:text-blue-800 underline">
                    Ver Compra #{payment.purchaseNumber || 'Auto-generada'} →
                  </a>
                </div>
              )}
              {showPurchaseLinks && isApproved && !payment.purchaseCreated && (
                <div className="text-sm print:text-xs text-amber-600 mt-1 print:mt-0.5 print:hidden">
                  Compra pendiente de crear
                </div>
              )}
            </div>

            <div className="text-right font-semibold">${formatWithThousandSep(payment.price)}</div>
          </React.Fragment>
        ))}
      </div>
    </div>
  )
}
export const GridPayment: React.FC<{ title: string; payments: Payment[] }> = ({ title, payments }) => {
  return (
    <div className="print:break-inside-avoid border border-gray-400 p-4 print:p-1 mt-4 print:mt-1">
      <div className="text-xl print:text-sm font-bold mb-2 print:mb-0 uppercase text-gray-700 border-b border-gray-300 pb-2 print:pb-0">
        {title}
      </div>
      <div className="grid grid-cols-4 text-base print:text-sm gap-y-2 print:gap-y-0 mt-2 print:mt-1">
        {payments.map((payment) => (
          <React.Fragment key={payment._id}>
            <div className="pr-2 col-span-2">{showPaymentWreserve(payment)}</div>
            <div className="pr-2 text-gray-600">{formatDateEx(payment.date)}</div>
            <div className="text-right font-semibold">${formatWithThousandSep(payment.value)}</div>
          </React.Fragment>
        ))}
      </div>
    </div>
  )
}
export const GridCost: React.FC<{ title: string; paycosts: Additional[] }> = ({ title, paycosts }) => {
  return (
    <div className="print:break-inside-avoid border border-gray-400 p-4 print:p-1 mt-4 print:mt-1">
      <div className="text-xl print:text-sm font-bold mb-2 print:mb-0 uppercase text-gray-700 border-b border-gray-300 pb-2 print:pb-0">
        {title}
      </div>
      <div className="grid grid-cols-4 text-base print:text-sm gap-y-2 print:gap-y-0 mt-2 print:mt-1">
        {paycosts.map((cost) => (
          <React.Fragment key={cost._id}>
            <div className="pr-2 col-span-2">{cost.description}</div>
            <div className="pr-2 text-gray-600">{formatDateEx(cost.date)}</div>
            <div className="text-right font-semibold">${formatWithThousandSep(cost.cost)}</div>
          </React.Fragment>
        ))}
      </div>
    </div>
  )
}

export default ReservationNvvDetail
